import { useContext, useState, useEffect } from 'react';
import { ArrowLeft, Save, ChevronDown, AlertCircle } from 'lucide-react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import AuthContext from '../../../../../../context/AuthContext';
import { useToast } from '../../../../../../context/ToastContext';
import recommendationService from '../../../../../../services/audit_services/recommendationService';
import auditUserManagementService from '../../../../../../services/audit_services/auditUserManagementService';
import auditQuestionService from '../../../../../../services/audit_services/auditQuestionService';
import userService from '../../../../../../services/userService';

export default function RecommendationCreate() {
  const { auditPlanId, sectionId, questionId } = useParams();
  const [searchParams] = useSearchParams();
  const recommendationIndex = searchParams.get('recommendation');
  const navigate = useNavigate();
  const { authTokens } = useContext(AuthContext);
  const { showError, showSuccess } = useToast();

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);

  // Question info and context data
  const [questionInfo, setQuestionInfo] = useState(null);
  const [auditPlan, setAuditPlan] = useState(null);
  const [userRole, setUserRole] = useState(null);

  // Owner search states
  const [ownerOptions, setOwnerOptions] = useState([]);
  const [ownerSearch, setOwnerSearch] = useState('');
  const [showOwnerDropdown, setShowOwnerDropdown] = useState(false);
  const [selectedOwner, setSelectedOwner] = useState(null);

  // Form data
  const [formData, setFormData] = useState({
    question_recommendation: '', // This will be the question recommendation ID
    recommendation_owner: '',
    delay_of_recommendation: '',
    elements_of_recommendation: '',
    kpi_of_recommendation: '',
  });

  const [errors, setErrors] = useState({});

  // Access control
  const canEdit = userRole === 'auditor' && auditPlan?.status === 'in_progress';

  useEffect(() => {
    if (questionId && auditPlanId && sectionId) {
      fetchQuestionInfo();
    } else {
      showError('Question, Audit Plan, and Section IDs are required');
      navigate('/dashboard');
    }
  }, [questionId, auditPlanId, sectionId]);

  const fetchQuestionInfo = async () => {
    setInitialLoading(true);
    
    try {
      const result = await auditQuestionService.getAuditQuestionDetail(
        authTokens.access, 
        auditPlanId, 
        sectionId, 
        questionId
      );
      
      if (result.success) {
        setQuestionInfo(result.data);
        setAuditPlan(result.data.audit_plan);
        setUserRole(result.data.my_role_in_audit_plan);
        
        // Check access control
        if (result.data.my_role_in_audit_plan !== 'auditor' || result.data.audit_plan.status !== 'in_progress') {
          showError('You do not have permission to create recommendation actions');
          navigate(`/dashboard/audit/audit-plans/${auditPlanId}/sections/${sectionId}/questions/${questionId}`);
          return;
        }
        
        // Check if observation effectiveness is false (required for recommendations)
        if (result.data.observation_effectiveness !== false) {
          showError('Recommendations can only be created for questions with ineffective observations');
          navigate(`/dashboard/audit/audit-plans/${auditPlanId}/sections/${sectionId}/questions/${questionId}`);
          return;
        }
        
        // Pre-populate the question recommendation field if available
        if (result.data.recommendations && result.data.recommendations.length > 0) {
          let recommendationText = '';
          let recommendationId = null;
          
          if (recommendationIndex !== null && !isNaN(recommendationIndex)) {
            const index = parseInt(recommendationIndex);
            if (result.data.recommendations[index]) {
              recommendationText = result.data.recommendations[index].recommendation || '';
              recommendationId = result.data.recommendations[index].id;
            }
          } else {
            // If no specific recommendation index, use the first one
            recommendationText = result.data.recommendations[0].recommendation || '';
            recommendationId = result.data.recommendations[0].id;
          }
          
          setFormData(prev => ({
            ...prev,
            question_recommendation: recommendationId,
            recommendation_text: recommendationText // Store for display
          }));
        }
      } else {
        showError(result.error || 'Error loading question details');
        navigate(`/dashboard/audit/audit-plans/${auditPlanId}/sections/${sectionId}/questions`);
      }
    } catch (error) {
      console.error('Error fetching question info:', error);
      showError('Error loading question details');
      navigate(`/dashboard/audit/audit-plans/${auditPlanId}/sections/${sectionId}/questions`);
    } finally {
      setInitialLoading(false);
    }
  };

  // Search users for owner dropdown
  const searchUsers = async (searchTerm) => {
    try {
      // const result = await auditUserManagementService.filterAuditUserSearch(authTokens.access, searchTerm);
      const result = await userService.filterUsersBySearch(searchTerm, authTokens.access);
      if (result.success) {
        setOwnerOptions(result.data || []);
      } else {
        console.error('User search failed:', result.error);
        setOwnerOptions([]);
      }
    } catch (error) {
      console.error('Error searching users:', error);
      setOwnerOptions([]);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleOwnerSearch = (value) => {
    setOwnerSearch(value);
    if (value.length >= 2) {
      searchUsers(value);
      setShowOwnerDropdown(true);
    } else {
      setOwnerOptions([]);
      setShowOwnerDropdown(false);
    }
  };

  const selectOwner = (owner) => {
    setSelectedOwner(owner);
    setOwnerSearch(`${owner.first_name} ${owner.last_name}`);
    setFormData(prev => ({ ...prev, recommendation_owner: owner.id }));
    setShowOwnerDropdown(false);
    setOwnerOptions([]);
    
    // Clear error
    if (errors.recommendation_owner) {
      setErrors(prev => ({ ...prev, recommendation_owner: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.recommendation_owner) {
      newErrors.recommendation_owner = 'Recommendation owner is required';
    }

    if (!formData.delay_of_recommendation) {
      newErrors.delay_of_recommendation = 'Target completion date is required';
    }

    if (!formData.elements_of_recommendation.trim()) {
      newErrors.elements_of_recommendation = 'Elements of recommendation is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      showError('Please fix the validation errors');
      return;
    }

    setLoading(true);

    try {
      const recommendationData = recommendationService.formatRecommendationData({
        ...formData,
        question: questionId // This is not used in the new format
      });

      const result = await recommendationService.createRecommendation(
        authTokens.access, 
        auditPlanId, 
        sectionId, 
        questionId, 
        recommendationData
      );

      if (result.success) {
        showSuccess('Recommendation action created successfully');
        navigate(`/dashboard/audit/audit-plans/${auditPlanId}/sections/${sectionId}/questions/${questionId}/recommendations/${result.data.id}`);
      } else {
        if (result.errorData && typeof result.errorData === 'object') {
          setErrors(result.errorData);
        }
        showError(result.error || 'Error creating recommendation action');
      }
    } catch (error) {
      console.error('Error creating recommendation:', error);
      showError('Error creating recommendation action');
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des détails de la question...</p>
        </div>
      </div>
    );
  }

  if (!questionInfo) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Question non trouvée</p>
        <Link 
          to={`/dashboard/audit/audit-plans/${auditPlanId}/sections/${sectionId}/questions`} 
          className="text-green-600 hover:underline mt-4 inline-block"
        >
          Retour aux questions
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to={`/dashboard/audit/audit-plans/${auditPlanId}/sections/${sectionId}/questions/${questionId}`}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Créer une action de recommandation</h1>
            <p className="text-sm text-gray-600">
              Pour la question: {questionInfo.question_name}
            </p>
          </div>
        </div>
      </div>

      {/* Access Control Banner */}
      {!canEdit && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
            <div>
              <p className="text-sm font-medium text-red-800">
                {userRole !== 'auditor' 
                  ? 'Seuls les auditeurs peuvent créer des actions de recommandation'
                  : 'Les actions de recommandation ne peuvent être créées que lorsque le plan d\'audit est en cours'
                }
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Question Context */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0">
            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
              <span className="text-green-600 text-sm font-semibold">Q</span>
            </div>
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-medium text-green-900">{questionInfo.question_name}</h3>
            {questionInfo.question_description && (
              <p className="text-sm text-green-700 mt-1">{questionInfo.question_description}</p>
            )}
            <div className="flex items-center space-x-4 mt-2 text-xs text-green-600">
              <span>Section: {questionInfo.audit_section?.section_name}</span>
              <span>Statut: {questionInfo.status}</span>
              <span>Efficacité de l'observation: Non</span>
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">Détails de l'action de recommandation</h2>
          
          <div className="space-y-6">
            {/* Question Recommendation (Read-only display) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Recommandation de la question
              </label>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <p className="text-sm text-gray-900">
                  {formData.recommendation_text || 'Aucun texte de recommandation disponible dans la question'}
                </p>
              </div>
            </div>

            {/* Recommendation Owner */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Propriétaire de la recommandation <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={ownerSearch}
                  onChange={(e) => handleOwnerSearch(e.target.value)}
                  onFocus={() => {
                    if (ownerOptions.length > 0) setShowOwnerDropdown(true);
                  }}
                  disabled={!canEdit}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed ${
                    errors.recommendation_owner ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Rechercher un utilisateur..."
                />
                <ChevronDown className="absolute right-3 top-2.5 h-5 w-5 text-gray-400" />
                
                {showOwnerDropdown && ownerOptions.length > 0 && canEdit && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {ownerOptions.map((owner) => (
                      <button
                        key={owner.id}
                        type="button"
                        onClick={() => selectOwner(owner)}
                        className="w-full px-3 py-2 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {owner.first_name} {owner.last_name}
                            </p>
                            <p className="text-xs text-gray-500">{owner.email}</p>
                          </div>
                          <span className="text-xs text-gray-400">{owner.role}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {errors.recommendation_owner && (
                <p className="mt-1 text-sm text-red-600">{errors.recommendation_owner}</p>
              )}
            </div>

            {/* Target Completion Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date d'achèvement cible <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={formData.delay_of_recommendation}
                onChange={(e) => handleInputChange('delay_of_recommendation', e.target.value)}
                disabled={!canEdit}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed ${
                  errors.delay_of_recommendation ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {errors.delay_of_recommendation && (
                <p className="mt-1 text-sm text-red-600">{errors.delay_of_recommendation}</p>
              )}
            </div>

            {/* Elements of Recommendation */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Éléments de la recommandation <span className="text-red-500">*</span>
              </label>
              <textarea
                value={formData.elements_of_recommendation}
                onChange={(e) => handleInputChange('elements_of_recommendation', e.target.value)}
                rows={4}
                disabled={!canEdit}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed ${
                  errors.elements_of_recommendation ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Décrivez les éléments et actions spécifiques requis pour cette recommandation..."
              />
              {errors.elements_of_recommendation && (
                <p className="mt-1 text-sm text-red-600">{errors.elements_of_recommendation}</p>
              )}
            </div>

            {/* KPI of Recommendation */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                KPI de la recommandation
              </label>
              <textarea
                value={formData.kpi_of_recommendation}
                onChange={(e) => handleInputChange('kpi_of_recommendation', e.target.value)}
                rows={3}
                disabled={!canEdit}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder="Définissez les indicateurs de performance clés pour mesurer le succès de cette recommandation..."
              />
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex items-center justify-end space-x-4">
          <Link
            to={`/dashboard/audit/audit-plans/${auditPlanId}/sections/${sectionId}/questions/${questionId}`}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Annuler
          </Link>
          {canEdit && (
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
            >
              <Save className="h-4 w-4 mr-2" />
              {loading ? 'Création...' : 'Créer l\'action de recommandation'}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}